import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useConversationStoreShallow } from 'src/store/conversationStore';

import { SkeletonConversationDetailsPage } from './ConversationDetailsPage.skeleton';

import { ModalsProvider } from '@speeki/global-ui-components';

import { InlineNotice } from '@components/atoms/InlineNotice/InlineNotice';
import { ConversationLayout } from '@components/templates/ConversationLayout/ConversationLayout';

import {
    CountryAnonymityEnabledDocument,
    TenantModulesDocument,
} from '@graphql/generated/graphql';

import { useResponsive } from '@utils/hooks/useResponsive';
import { normaliseEmail } from '@utils/reporterEmail';

import { useQuery, useReactiveVar } from '@apollo/client';
import {
    anonymityStatusVar,
    authVar,
    tenantModulesVar,
    verifiedReporterEmailsVar,
} from '@boot/reactiveVariables';
import { ConversationDetailsForm } from '@domains/new-conversation/organisms/ConversationDetailsForm/ConversationDetailsForm';
import { ReportDisclosureSummary } from '@domains/new-conversation/organisms/ReportDisclosureSummary/ReportDisclosureSummary';
import {
    ConversationText,
    ConversationTitle,
    ProceedButton,
} from '@domains/new-conversation/pages/Conversation.styled';
import { useConversationForm } from '@domains/new-conversation/useConversationForm';

import {
    ContentWrapper,
    MultilineTextWrppaer,
} from './ConversationDetailsPage.styled';

export const ConversationDetailsPage = () => {
    const loadingAnonymity = useReactiveVar(anonymityStatusVar) === 'loading';
    const { isGuest, isLoggedIn } = useReactiveVar(authVar);

    const { clearConversationMessages } = useConversationStoreShallow([
        'clearConversationMessages',
    ]);

    const isMd = useResponsive('md');
    const { t } = useTranslation();

    const { data: tenantModulesData } = useQuery(TenantModulesDocument, {
        onCompleted: ({ tenant }) => {
            tenantModulesVar(tenant.modules);
        },
        skip: !isLoggedIn,
    });
    const { data: countryAnonymityData } = useQuery(
        CountryAnonymityEnabledDocument,
        { context: { notifyOnError: true }, fetchPolicy: 'network-only' },
    );
    const subsidiarySitesEnabled =
        !!tenantModulesData?.tenant.modules.subsidiarySites;

    const { loading, methods, onSubmit, startCaseLoading } =
        useConversationForm();

    const email = methods.watch('email');
    const firstName = methods.watch('firstName');
    const lastName = methods.watch('lastName');
    const verifiedEmails = useReactiveVar(verifiedReporterEmailsVar);

    /*
     * An address the reporter typed but never confirmed holds up the report,
     * in BOTH modes (decided 2026-08-08). Where the address is optional the
     * reporter can remove it and carry on; where it is mandatory there is no
     * removal exit, so a reporter whose mailbox cannot receive the code
     * cannot file - accepted, in exchange for a mandatory identity always
     * being a proven one.
     */
    const awaitingEmailConfirmation =
        !!email && !verifiedEmails.includes(normaliseEmail(email));

    useEffect(() => {
        clearConversationMessages();
    }, []);

    const {
        formState: { errors, isSubmitted, isSubmitting, isValid },
        handleSubmit,
    } = methods;

    const isError = !!Object.keys(errors).length && isSubmitted;

    /*
     * Everything other than the unconfirmed address that can hold Proceed
     * back. The disabled state and the sole-blocker notice below both derive
     * from this one expression, so they cannot drift apart: the notice shows
     * exactly when the button is disabled for the address alone.
     */
    const otherBlockers =
        loading || isSubmitting || (!isValid && isSubmitted) || isError;

    /*
     * A disabled button explains nothing on its own. When the unconfirmed
     * address is the ONLY thing holding Proceed back, say so next to the
     * button; while any other blocker is active the notice would misdirect,
     * so it stays away. Mandatory mode has no removal exit, so its variant
     * does not offer one.
     */
    const isBlockedOnlyByEmailConfirmation =
        awaitingEmailConfirmation && !errors.email && !otherBlockers;
    const isEmailMandatory = !!methods.watch('personalInfoMandatory');

    useEffect(() => {
        if (subsidiarySitesEnabled) {
            ModalsProvider.open('SelectSubsidiarySitesModal');
        }
    }, [subsidiarySitesEnabled]);

    return (
        <ConversationLayout
            sidebarProps={{
                buttons: (
                    <>
                        {isBlockedOnlyByEmailConfirmation && (
                            <InlineNotice
                                testId="notice_email-confirm-blocking-proceed"
                                variant={'warning'}
                            >
                                {t(
                                    isEmailMandatory
                                        ? 'REPORTER_EMAIL_CONFIRM_BLOCKING_PROCEED_MANDATORY'
                                        : 'REPORTER_EMAIL_CONFIRM_BLOCKING_PROCEED',
                                )}
                            </InlineNotice>
                        )}
                        <ProceedButton
                            disabled={
                                otherBlockers || awaitingEmailConfirmation
                            }
                            isLoading={startCaseLoading}
                            onClick={handleSubmit(onSubmit)}
                            testId="button_start-conversation"
                            variant={'primary'}
                        >
                            {t('START_CONVERSATION')}
                        </ProceedButton>
                    </>
                ),
                currentStep: 1,
            }}
        >
            <ConversationTitle>
                {t('NEW_CONVERSATION_DETAILS_TITLE')}
            </ConversationTitle>
            <ContentWrapper>
                {isMd ? (
                    <MultilineTextWrppaer>
                        <ConversationText>
                            {t('NEW_CONVERSATION_TEXT_1')}
                        </ConversationText>
                        {countryAnonymityData?.isEnabled && (
                            <ConversationText>
                                {t('NEW_CONVERSATION_TEXT_2')}
                            </ConversationText>
                        )}
                    </MultilineTextWrppaer>
                ) : (
                    <ConversationText>
                        {t('NEW_CONVERSATION_DETAILS_INFO')}
                    </ConversationText>
                )}

                {loading || loadingAnonymity ? (
                    <SkeletonConversationDetailsPage />
                ) : (
                    <>
                        <FormProvider {...methods}>
                            <ConversationDetailsForm />
                        </FormProvider>
                        {/* Beside the fields it describes, fed by what they
                         * hold right now - the reactive var behind the
                         * summary is only written at submission. */}
                        <ReportDisclosureSummary
                            hasUpdatesStep={isGuest}
                            liveDetails={{
                                email,
                                firstName,
                                lastName,
                            }}
                        />
                    </>
                )}
            </ContentWrapper>
        </ConversationLayout>
    );
};
